/** The banner a FAILED proposal shows, with its retry path.
 *
 * Apply is all-or-nothing (the change engine, AIE §2): a failed apply wrote
 * nothing. The failing item carries its error; "Return to review" puts the
 * proposal back in review with its failed items `proposed` again, so the
 * reviewer can fix them, re-accept and apply. The backend refuses (409) a
 * proposal that was partially applied before the all-or-nothing engine — the
 * refusal's reason is shown as-is.
 *
 * A failure of the whole apply (the change set's S3 write, its entry cap) names
 * no item: its error is on the apply task's result, shown here (review C8-5). */
import UndoIcon from '@mui/icons-material/Undo';
import { Alert, Button } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';

import * as bgtasksApi from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import * as proposalsApi from '@/api/compositionProposals';
import { useSnackbar } from '@/components/Snackbar';

export function FailedApplyBanner({
  proposalId,
  canManage,
  onReturned,
  applyTaskId = null,
}: {
  proposalId: number;
  canManage: boolean;
  onReturned: () => void;
  /** The failed apply's task, whose archived result carries a whole-apply error. */
  applyTaskId?: string | null;
}) {
  const snackbar = useSnackbar();
  // Same query as CompositionResultPanel's: the archive lands slightly after the task ends.
  const resultQuery = useQuery({
    queryKey: ['bgtask-result', applyTaskId],
    queryFn: () => bgtasksApi.result(applyTaskId!),
    enabled: !!applyTaskId,
    staleTime: Infinity,
    retry: (failureCount) => failureCount < 8,
    retryDelay: 1_500,
  });
  const result = resultQuery.data?.result;
  const wholeApplyError =
    result?.action === 'apply' &&
    Object.keys((result.failed as Record<string, unknown>) ?? {}).length === 0
      ? ((result.error as string | undefined) ?? '')
      : '';
  const mutation = useMutation({
    mutationFn: () => proposalsApi.returnToReview(proposalId),
    onSuccess: (data) => {
      const n = data.items_reset.length;
      snackbar.show(
        `Back in review — ${n} failed item${n === 1 ? '' : 's'} reset to proposed. Fix, re-accept, then apply.`,
        'success',
      );
      onReturned();
    },
    onError: (e) => {
      const detail =
        e instanceof ConsoleApiError
          ? ((e.body?.detail as string | undefined) ?? e.message)
          : 'Return to review failed.';
      snackbar.show(detail, 'error');
    },
  });

  return (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        canManage ? (
          <Button
            color="inherit"
            size="small"
            startIcon={<UndoIcon />}
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Return to review
          </Button>
        ) : undefined
      }
    >
      {wholeApplyError
        ? `The apply failed and was rolled back — nothing was applied: ${wholeApplyError}. Return the ` +
          'proposal to review, then apply it again once the cause is fixed.'
        : 'The apply failed and was rolled back — nothing was applied. The failed item carries its error ' +
          'below. Return the proposal to review, fix the item, re-accept it, then apply again.'}
    </Alert>
  );
}
